import { useMemo } from 'react';
import { ENUM_LABELS, type StockMovementRow, toOptions } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { DensityControl } from '#client/components/ui/DensityControl';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { Pagination } from '#client/components/ui/Pagination';
import { SearchFilter } from '#client/components/ui/SearchFilter';
import { Select } from '#client/components/ui/Select';
import { useResourceList } from '#client/hooks/useResourceList';
import { useUrlState } from '#client/hooks/useUrlState';
import { ENDPOINT } from '#client/lib/endpoints';
import { routes } from '#client/lib/routes';
import { stockMovementColumns } from './inventory-columns';
import { countOf } from '#client/lib/format';
import { ButtonLink } from '#client/components/ui/ButtonLink';

const KIND_OPTIONS = toOptions(ENUM_LABELS.stockMovementKind);

/**
 * The stock ledger.
 *
 * Append-only, so there is no edit, no delete and no create — a wrong movement
 * is corrected with an opposing adjustment carrying a reason, and both rows
 * stay. That is the whole point: the sum of this table *is* the stock, and a
 * row that could be rewritten would make it an opinion.
 */
export function StockLedgerPage() {
  const { params, setParam } = useUrlState();
  const kind = params.get('kind');

  const extraQuery = useMemo(() => (kind ? { kind } : {}), [kind]);
  const movements = useResourceList<StockMovementRow>({
    path: ENDPOINT.stockMovements,
    extraQuery,
  });

  return (
    <Page
      title="Stock ledger"
      subtitle="Every movement, append-only. Corrections are new rows, never edits."
      fullWidth
      backAction={
        <ButtonLink variant="secondary" size="md" to={routes.reagentItems}>
          Back to inventory
        </ButtonLink>
      }
    >
      <Card flush>
        <div className="c-table-toolbar">
          <div className="c-table-toolbar__search">
            <SearchFilter
              value={movements.search}
              onChange={movements.setSearch}
              placeholder="Search by reagent, lot, report or reason"
            />
          </div>
          <Select
            label="Movement"
            hideLabel
            options={KIND_OPTIONS}
            placeholder="All movements"
            value={kind ?? ''}
            onChange={(choice) => setParam('kind', choice || undefined)}
          />
          <span className="c-table-toolbar__count">{countOf(movements.count, 'movement')}</span>
          <DensityControl />
        </div>

        <DataTable
          columns={stockMovementColumns}
          rows={movements.items}
          rowKey={(row) => row.id}
          loading={movements.loading}
          caption="Stock movements"
          emptyState={
            <EmptyState
              title="No movements yet"
              body="Entering stock or running a test writes a row here."
            />
          }
        />

        <Pagination
          page={movements.page}
          limit={movements.limit}
          count={movements.count}
          onPageChange={movements.setPage}
          label="movements"
        />
      </Card>
    </Page>
  );
}
